import {
  cloneElement,
  FunctionComponentElement,
  MutableRefObject,
} from "react";
import Portal from "./Portal";
import { THEME_COLOR_DARK_ALT } from "../vars";
import { useTooltip, TooltipPopup, Position } from "@reach/tooltip";
import "@reach/tooltip/styles.css";

// This comes from an un-exported type in @reach/tooltip
type Rect =
  | null
  | undefined
  | (Partial<DOMRect> & {
      readonly bottom: number;
      readonly height: number;
      readonly left: number;
      readonly right: number;
      readonly top: number;
      readonly width: number;
    });

const centered: Position = (triggerRect: Rect, tooltipRect: Rect) => {
  if (!triggerRect) return { top: 0, left: 0 };
  if (!tooltipRect) return { top: 0, left: 0 };

  const triggerCenter = triggerRect.left + triggerRect.width / 2;
  const left = triggerCenter - tooltipRect.width / 2;
  const maxLeft = window.innerWidth - tooltipRect.width - 2;
  return {
    left: Math.min(Math.max(2, left), maxLeft) + window.scrollX,
    top: triggerRect.bottom + 8 + window.scrollY,
  };
};

function mergeRefs<T = any>(
  refs: Array<React.MutableRefObject<T> | React.LegacyRef<T>>
): React.RefCallback<T> {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") {
        ref(value);
      } else if (ref !== null) {
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    });
  };
}

export default function Tooltip({
  children,
  disabled,
  label,
  ariaLabel = "3",
}: {
  children: FunctionComponentElement<any>;
  disabled?: boolean;
  label: string;
  ariaLabel?: string;
}) {
  const [trigger, tooltip] = useTooltip();
  const { isVisible, triggerRect } = tooltip;

  return (
    <>
      {cloneElement(children, {
        ...trigger,
        ...children.props,
        ref: mergeRefs([children.ref as MutableRefObject<any>, trigger.ref]),
      })}
      {!disabled && (
        <>
          {isVisible && (
            // The Triangle. We position it relative to the trigger, not the popup
            // so that collisions don't have a triangle pointing off to nowhere.
            // Using a Portal may seem a little extreme, but we can keep the
            // positioning logic simpler here instead of needing to consider
            // the popup's position relative to the trigger and collisions
            <Portal>
              <div
                style={{
                  position: "absolute",
                  left: triggerRect
                    ? triggerRect.left - 10 + triggerRect.width / 2
                    : undefined,
                  top: triggerRect
                    ? triggerRect.bottom + window.scrollY
                    : undefined,
                  width: 0,
                  height: 0,
                  borderLeft: "10px solid transparent",
                  borderRight: "10px solid transparent",
                  borderBottom: `10px solid ${THEME_COLOR_DARK_ALT}`,
                  zIndex: 10000000000,
                  pointerEvents: "none",
                }}
              />
            </Portal>
          )}
          <TooltipPopup
            {...tooltip}
            label={label}
            ariaLabel={ariaLabel}
            style={{
              background: `${THEME_COLOR_DARK_ALT}`,
              color: "white",
              border: "none",
              borderRadius: "3px",
              padding: "0.5em 1em",
              zIndex: 10000000000,
              pointerEvents: "none",
            }}
            position={centered}
          />
        </>
      )}
    </>
  );
}
